import clsx from "clsx";
import type { FunctionComponent } from "react";
import { useMemo } from "react";

import CompanyLogo from "~/components/CompanyLogo";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import { statusVariants } from "~/components/statusDisplay/Status/statusVariants.ts";
import { getInvoiceStatusDisplayNameById } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { getInvoiceStatusIcon } from "~/modules/billing/components/InvoicesIndexView/invoicesIndexUtils.tsx";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import type { AvatarImage } from "~/modules/image/api/avatarImage/avatarImageTypes.ts";

type Props = {
	clientFilterValue: string[]
	onChangeClientFilter: (value: string[]) => void;
	onChangeStatusFilter: (value: string[]) => void;
	onRemoveOverdueFilter: () => void,
	showOverdueOnly: boolean;
	statusFilterValue: string[];
};

const InvoicesIndexActiveFilters: FunctionComponent<Props> = ({
	clientFilterValue,
	onChangeClientFilter,
	onChangeStatusFilter,
	onRemoveOverdueFilter,
	showOverdueOnly,
	statusFilterValue,
}) => {
	const {
		data: allClientsData,
		isLoading: isLoadingAllClients,
	} = useAllCompanies();

	const clientNamesKeyed = useMemo(() => {
		if (allClientsData) {
			return allClientsData.reduce((acc, client) => {
				acc[client.id] = { displayName: client.displayName, avatarImage: client.avatarImage };
				return acc;
			}, {} as Record<string, { displayName: string, avatarImage: AvatarImage | null }>);
		}
		return {};
	}, [allClientsData]);

	if (clientFilterValue.length === 0 && statusFilterValue.length === 0 && !showOverdueOnly) {
		return null;
	}

	return (
		<div className="z-10 flex w-full pb-2 pt-4">
			<div className="ml-4 mr-2 shrink-0 text-sm text-gray-600">Aktive Filter:</div>
			<div className="ml-1">
				<div className="-m-1 flex flex-wrap items-center gap-1">
					{statusFilterValue.map((invoiceStatusId,
						index) => {
						return <span
							key={"statusFilter" + index}
							className={clsx(`inline-flex items-center gap-x-1 
										rounded-full
										py-1.5 pl-3 pr-2 
										text-sm font-medium`, statusVariants({
								theme: "white",
								borderRadius: "full",
								disabled: showOverdueOnly,
							}))}
						>
										{getInvoiceStatusIcon(invoiceStatusId as InvoiceStatusId, "size-5")} {getInvoiceStatusDisplayNameById(invoiceStatusId as InvoiceStatusId)}
							<button
								type="button"
								className="ml-1 inline-flex size-4 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
								onClick={() => onChangeStatusFilter([...statusFilterValue].filter(status => status !== invoiceStatusId))}
							>
											<span className="sr-only">Remove filter for status option</span>
											<svg
												className="size-2"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 8 8"
											>
												<path strokeLinecap="round"
													  strokeWidth="1.5"
													  d="M1 1l6 6m0-6L1 7" />
											</svg>
										</button>
									</span>;
					})}
					{clientFilterValue.map((clientId,
						index) => {
						return <span
							key={"statusFilter" + index}
							className={clsx(`inline-flex items-center gap-x-1 
										rounded-full
										py-1.5 pl-3 pr-2 
										text-sm font-medium`, statusVariants({
								theme: "white",
								borderRadius: "full",
								disabled: showOverdueOnly,
							}))}
						>
							{isLoadingAllClients && <LoadingSpinner theme="neutral"
																	scale={0.5} />}
							{!isLoadingAllClients && clientNamesKeyed[clientId]?.avatarImage &&
								<CompanyLogo image={clientNamesKeyed[clientId].avatarImage}
											 sizeClassName="size-4" />}
							{!isLoadingAllClients && clientNamesKeyed[clientId]?.displayName}
							<button
								type="button"
								className="ml-1 inline-flex size-4 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
								onClick={() => onChangeClientFilter([...clientFilterValue].filter(filterClientId => filterClientId !== clientId))}
							>
											<span className="sr-only">Remove filter for status option</span>
											<svg
												className="size-2"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 8 8"
											>
												<path strokeLinecap="round"
													  strokeWidth="1.5"
													  d="M1 1l6 6m0-6L1 7" />
											</svg>
										</button>
									</span>;
					})}
					{showOverdueOnly && <span
						key={"statusFilter-overdue"}
						className={clsx(`inline-flex items-center gap-x-1 
										rounded-full
										py-1.5 pl-3 pr-2 
										text-sm font-medium`, statusVariants({
							theme: "info",
							borderRadius: "full",
						}))}
					>
								Alle überfälligen Rechnungen (Andere Filter sind temporär deaktiviert)
						<button
							type="button"
							className="ml-1 inline-flex size-4 shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
							onClick={onRemoveOverdueFilter}
						>
											<span className="sr-only">Remove filter for status option</span>
											<svg
												className="size-2"
												stroke="currentColor"
												fill="none"
												viewBox="0 0 8 8"
											>
												<path strokeLinecap="round"
													  strokeWidth="1.5"
													  d="M1 1l6 6m0-6L1 7" />
											</svg>
										</button>
									</span>
					}
				</div>
			</div>
		</div>);
};

export default InvoicesIndexActiveFilters;